import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UseCaseSummary } from '../../types';

export interface MatrixBuckets {
  quickWins: UseCaseSummary[];
  strategic: UseCaseSummary[];
  easy: UseCaseSummary[];
  reconsider: UseCaseSummary[];
}

export interface DecisionMatrixCardProps {
  /** Matrix buckets containing use cases grouped by quadrant */
  matrixBuckets: MatrixBuckets;
  /** Current category filter value */
  categoryFilter: string | 'all';
  /** Total number of use cases (for category filter display) */
  totalUseCases: number;
  /** Sorted categories with counts for filter buttons */
  sortedCategories: [string, number][];
  /** Callback when category filter changes */
  onCategoryFilterChange: (category: string | 'all') => void;
}

export const DecisionMatrixCard: React.FC<DecisionMatrixCardProps> = ({
  matrixBuckets,
  categoryFilter,
  totalUseCases,
  sortedCategories,
  onCategoryFilterChange,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-border bg-card/80 p-4 shadow-sm overflow-hidden flex flex-col">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-3 flex-shrink-0">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('useCases.matrix.decisionTitle')}</h2>
          <p className="text-xs text-muted-foreground">
            {t('useCases.matrix.decisionHelp')}
          </p>
        </div>
        {/* Category tabs */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              categoryFilter === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            onClick={() => onCategoryFilterChange('all')}
          >
            {t('global.all')}
            {totalUseCases ? <span className="ml-1 text-[10px] opacity-80">({totalUseCases})</span> : null}
          </button>
          {sortedCategories.map(([cat, count]) => (
            <button
              key={cat}
              type="button"
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                categoryFilter === cat
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              onClick={() => onCategoryFilterChange(cat)}
            >
              {cat}
              <span className="ml-1 text-[10px] opacity-80">({count})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-2 items-stretch grid-cols-2 grid-rows-2 flex-1 min-h-0 overflow-hidden">
        {/* Quick Wins */}
        <div className="flex h-full flex-col rounded-xl border border-border bg-muted/60 p-3 shadow-sm overflow-hidden max-h-[200px]">
          <div className="mb-2 flex items-center justify-between border-b border-amber-500/30 pb-1.5">
            <div>
              <h3 className="text-xs font-semibold text-foreground">{t('useCases.matrix.quickWins.title')}</h3>
            </div>
            <span className="rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-400">
              {matrixBuckets.quickWins.length}
            </span>
          </div>
          <div className="flex-1 space-y-1 overflow-y-auto pr-1">
            {matrixBuckets.quickWins.map((uc) => (
              <button
                key={uc.id}
                type="button"
                onClick={() => navigate(`/cas-usage/${uc.id}`)}
                className="w-full rounded-lg border border-border/60 bg-card/90 p-2 text-left shadow-sm transition hover:border-amber-400/80 hover:shadow-md"
              >
                <div className="text-xs font-semibold text-foreground line-clamp-1">{uc.name}</div>
                <div className="mt-1 text-[10px] text-amber-300">
                  I:{uc.value_score ?? '—'} C:{uc.complexity_score ?? '—'}
                </div>
              </button>
            ))}
            {matrixBuckets.quickWins.length === 0 && (
              <p className="text-[10px] text-muted-foreground">
                {t('useCases.matrix.emptyZone')}
              </p>
            )}
          </div>
        </div>

        {/* Stratégique */}
        <div className="flex h-full flex-col rounded-xl border border-border bg-muted/60 p-3 shadow-sm overflow-hidden max-h-[200px]">
          <div className="mb-2 flex items-center justify-between border-b border-sky-500/30 pb-1.5">
            <div>
              <h3 className="text-xs font-semibold text-foreground">{t('useCases.matrix.strategic.title')}</h3>
            </div>
            <span className="rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-300">
              {matrixBuckets.strategic.length}
            </span>
          </div>
          <div className="flex-1 space-y-1 overflow-y-auto pr-1">
            {matrixBuckets.strategic.map((uc) => (
              <button
                key={uc.id}
                type="button"
                onClick={() => navigate(`/cas-usage/${uc.id}`)}
                className="w-full rounded-lg border border-border/60 bg-card/90 p-2 text-left shadow-sm transition hover:border-sky-400/80 hover:shadow-md"
              >
                <div className="text-xs font-semibold text-foreground line-clamp-1">{uc.name}</div>
                <div className="mt-1 text-[10px] text-sky-300">
                  I:{uc.value_score ?? '—'} C:{uc.complexity_score ?? '—'}
                </div>
              </button>
            ))}
            {matrixBuckets.strategic.length === 0 && (
              <p className="text-[10px] text-muted-foreground">
                {t('useCases.matrix.emptyZone')}
              </p>
            )}
          </div>
        </div>

        {/* Faciles */}
        <div className="flex h-full flex-col rounded-xl border border-border bg-muted/60 p-3 shadow-sm overflow-hidden max-h-[200px]">
          <div className="mb-2 flex items-center justify-between border-b border-violet-500/30 pb-1.5">
            <div>
              <h3 className="text-xs font-semibold text-foreground">{t('useCases.matrix.easy.title')}</h3>
            </div>
            <span className="rounded-full bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-medium text-violet-300">
              {matrixBuckets.easy.length}
            </span>
          </div>
          <div className="flex-1 space-y-1 overflow-y-auto pr-1">
            {matrixBuckets.easy.map((uc) => (
              <button
                key={uc.id}
                type="button"
                onClick={() => navigate(`/cas-usage/${uc.id}`)}
                className="w-full rounded-lg border border-border/60 bg-card/90 p-2 text-left shadow-sm transition hover:border-violet-400/80 hover:shadow-md"
              >
                <div className="text-xs font-semibold text-foreground line-clamp-1">{uc.name}</div>
                <div className="mt-1 text-[10px] text-violet-300">
                  I:{uc.value_score ?? '—'} C:{uc.complexity_score ?? '—'}
                </div>
              </button>
            ))}
            {matrixBuckets.easy.length === 0 && (
              <p className="text-[10px] text-muted-foreground">
                {t('useCases.matrix.emptyZone')}
              </p>
            )}
          </div>
        </div>

        {/* À reconsidérer */}
        <div className="flex h-full flex-col rounded-xl border border-border bg-muted/60 p-3 shadow-sm overflow-hidden max-h-[200px]">
          <div className="mb-2 flex items-center justify-between border-b border-slate-500/30 pb-1.5">
            <div>
              <h3 className="text-xs font-semibold text-foreground">{t('useCases.matrix.reconsider.title')}</h3>
            </div>
            <span className="rounded-full bg-slate-500/10 px-1.5 py-0.5 text-[10px] font-medium text-slate-300">
              {matrixBuckets.reconsider.length}
            </span>
          </div>
          <div className="flex-1 space-y-1 overflow-y-auto pr-1">
            {matrixBuckets.reconsider.map((uc) => (
              <button
                key={uc.id}
                type="button"
                onClick={() => navigate(`/cas-usage/${uc.id}`)}
                className="w-full rounded-lg border border-border/60 bg-card/90 p-2 text-left shadow-sm transition hover:border-slate-400/80 hover:shadow-md"
              >
                <div className="text-xs font-semibold text-foreground line-clamp-1">{uc.name}</div>
                <div className="mt-1 text-[10px] text-slate-300">
                  I:{uc.value_score ?? '—'} C:{uc.complexity_score ?? '—'}
                </div>
              </button>
            ))}
            {matrixBuckets.reconsider.length === 0 && (
              <p className="text-[10px] text-muted-foreground">
                {t('useCases.matrix.emptyZone')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
